// Librairies
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs';
import { ConfirmComponent } from 'ui-library';
import { TemplateSaveDialogComponent, TemplateSaveDialogResult } from 'src/app/components/shared/dialogs/template-save-dialog/template-save-dialog.component';

// Interfaces
import { Tracker } from 'src/app/interfaces/api/tracker';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';

// Classes
import { Pop } from './pop';
import { isDuplicateTemplateName } from './tracker-config-display.util';
import { cloneSettings, TrackerConfigTool } from './tracker-config.tool';

// Shared by every per-device config editor (Ressources > Dispositif, Rapport KYD's dialog, KYD
// bulk config, add/edit tracker modals): the draft edited by TrackerConfigFormComponent, the
// template it was prefilled from, and the "Enregistrer"/"Enregistrer comme modèle" template
// prompts. Must be created in an injection context (a component field initializer).
export class TrackerTemplateEditor {
  // Services
  private readonly tool = inject(TrackerConfigTool);
  private readonly dialog = inject(MatDialog);
  private readonly pop = inject(Pop);
  private readonly translate = inject(TranslateService);

  // Variables
  configs: TrackerConfig[] = [];
  draft: TrackerConfig;
  appliedTemplateId?: string;
  // The applied template as it was when picked - to detect if the draft has diverged from it.
  private snapshot?: { settings: any; offlineDelay?: number };

  constructor(type: TrackerConfig['type'] = 'Unit', settings: any = {}) {
    this.draft = { id: '', name: '', type, settings };
  }

  // Templates of the draft's own type - the draft's type can change on the add/edit modals.
  get templates(): TrackerConfig[] {
    return this.configs.filter(c => c.type === this.draft.type);
  }

  get appliedTemplate(): TrackerConfig | undefined {
    return this.templates.find(c => c.id === this.appliedTemplateId);
  }

  // Draft = the tracker's own settings, completed with its linked template's defaults.
  loadTracker(tracker: Tracker) {
    const applied = this.tool.linkedConfig(tracker, this.configs);
    this.draft = {
      id: '', name: '', type: this.tool.typeOf(tracker) as TrackerConfig['type'],
      settings: this.tool.initialSettings(tracker, applied),
      offlineDelay: tracker.offlineDelay ?? applied?.offlineDelay,
    };
    this.setApplied(applied);
  }

  applyTemplate(configId: string | undefined) {
    // An unknown id (e.g. a "no template" option) just unlinks, leaving the values as they are.
    const template = this.templates.find(c => c.id === configId);
    if (!template) return this.setApplied(undefined);

    this.draft = { ...this.draft, settings: cloneSettings(template.settings ?? {}), offlineDelay: template.offlineDelay };
    this.setApplied(template);
  }

  // "Enregistrer": if the draft diverged from its template, asks whether to update the template
  // too (declining unlinks it), then runs `save`.
  save(save: () => void) {
    const applied = this.appliedTemplate;
    if (!applied || !this.hasDiverged()) return save();

    this.dialog.open(ConfirmComponent, {
      width: '500px',
      data: {
        title: this.translate.instant('trackers.template_diverged_confirm_title'),
        content: this.translate.instant('trackers.template_diverged_confirm_content', { name: applied.name }),
        okText: 'trackers.template_diverged_confirm_ok',
        cancelText: 'trackers.template_diverged_confirm_cancel',
      }
    }).afterClosed().subscribe(result => {
      if (!result?.ok) {
        this.setApplied(undefined);
        return save();
      }

      this.tool.saveAsTemplate(this.templateFromDraft(applied.id, applied.name)).subscribe({
        next: () => save(),
        error: (error: any) => this.saveError(error),
      });
    });
  }

  // "Enregistrer comme modèle": creates or updates a template from the draft, then runs `saved`
  // once it's the applied one.
  saveAsTemplate(saved: () => void) {
    const applied = this.appliedTemplate;
    if (applied && !this.hasDiverged()) {
      this.pop.toast(this.translate.instant('resources.template_up_to_date'), 'success', 3000, 'check');
      return;
    }

    this.dialog.open(TemplateSaveDialogComponent, { width: '450px', data: { appliedTemplateName: applied?.name } })
      .afterClosed().subscribe((result: TemplateSaveDialogResult) => {
        if (!result) return;

        if (result.mode === 'create' && isDuplicateTemplateName(this.configs, result.name)) {
          this.pop.warning(this.translate.instant('trackers.template_name_already_exists'));
          return this.saveAsTemplate(saved);
        }

        // 'update' is only offered when a template is applied (see appliedTemplateName above).
        const { id, name } = result.mode === 'create' ? { id: '', name: result.name } : { id: applied?.id ?? '', name: applied?.name ?? '' };

        this.tool.saveAsTemplate(this.templateFromDraft(id, name)).pipe(switchMap(() => this.tool.fetchConfigs())).subscribe({
          next: configs => {
            this.configs = configs ?? [];
            this.setApplied(this.configs.find(c => c.name === name));
            this.pop.toast(this.translate.instant('resources.template_saved'), 'success', 4000, 'save');
            saved();
          },
          error: (error: any) => this.saveError(error),
        });
      });
  }

  saveError(error: any) {
    this.pop.error(error?.error?.error ?? this.translate.instant('trackers.error.config_save'));
  }

  private setApplied(template: TrackerConfig | undefined) {
    this.appliedTemplateId = template?.id;
    this.snapshot = template ? { settings: cloneSettings(template.settings ?? {}), offlineDelay: template.offlineDelay } : undefined;
  }

  private hasDiverged(): boolean {
    return this.tool.hasDiverged(this.draft.settings, this.draft.offlineDelay, this.snapshot?.settings, this.snapshot?.offlineDelay);
  }

  private templateFromDraft(id: string, name: string): TrackerConfig {
    return { id, name, type: this.draft.type, settings: cloneSettings(this.draft.settings ?? {}), offlineDelay: this.draft.offlineDelay };
  }
}
